import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GraduationCap, PlayCircle, Megaphone, CalendarClock, Sparkles, CheckCircle2, Clock, Circle, HelpCircle, Video, Loader2, Quote, CalendarPlus, Paperclip, Lock, BookOpen, Send, X, Library, ClipboardCheck, Radio, Newspaper, Clapperboard, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { campusApi, apiError, mediaUrl, connectApi, isParentView } from "../lib/api";
import { ParentViewBar } from "../components/ParentViewBar";
import { renderRich } from "../lib/richText";
import { EvalInstructorForm } from "../components/campus/EvalInstructorForm";

const MONTHS = ["", "enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const fmtMonth = (ym) => { if (!ym) return "—"; const [y, m] = ym.split("-"); return `${MONTHS[Number(m)] || ""} ${y}`; };

const STATUS = {
  cumplida: { label: "Cumplida", cls: "bg-[#E2F6EB] text-[#22A860]", Icon: CheckCircle2 },
  vista: { label: "Vista · pendiente evidencia", cls: "bg-[#FBF0CE] text-[#B9860B]", Icon: Clock },
  pendiente: { label: "Pendiente de ver", cls: "bg-[#EDF0F4] text-[#6B7688]", Icon: Circle },
};

const ENTREGA_STATUS = {
  en_revision: { label: "En revisión", cls: "bg-[#FBF0CE] text-[#B9860B]" },
  valida: { label: "Aprobada", cls: "bg-[#E2F6EB] text-[#22A860]" },
  rechazada: { label: "Por corregir", cls: "bg-[#FBECEC] text-[#C24141]" },
};

const BLOG_VIDEO_PROGS = ["supera mini", "supera kids", "supera plus", "supera high", "supera pro", "supera universitario", "coach junior supera", "head coach junior supera"];
export const showBlogVideo = (programa) => BLOG_VIDEO_PROGS.includes((programa || "").trim().toLowerCase());

function embedSrc(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const gd = url.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  if (gd) return `https://drive.google.com/file/d/${gd[1]}/preview`;
  return mediaUrl(url);
}
const isInternalVideo = (url) => /\.(mp4|webm|mov|m4v)$/i.test(url || "");

// Render seguro de texto enriquecido: **negrita**, *cursiva*, ## Subtítulo, - listas.
function CommentBox({ estado, feedback, puntos, onSubmit, kind }) {
  const readOnly = isParentView();
  const [texto, setTexto] = useState("");
  const [busy, setBusy] = useState(false);
  const enviar = async () => {
    if (texto.trim().length < 3) { toast.error("Escribe tu comentario."); return; }
    setBusy(true);
    try { await onSubmit(texto.trim()); setTexto(""); } finally { setBusy(false); }
  };
  if (readOnly) return null;
  if (estado === "valida") return <div className="rounded-xl bg-[#E2F6EB] text-[#188A4E] font-bold text-sm p-3" data-testid={`${kind}-aprobado`}>✅ ¡Aprobado! Se sumaron tus {puntos || 5} Puntos Supera.</div>;
  if (estado === "en_revision") return <div className="rounded-xl bg-[#FBF0CE] text-[#B9860B] font-bold text-sm p-3" data-testid={`${kind}-revision`}>⏳ Tu comentario está en revisión del staff Supera.</div>;
  return (
    <div className="rounded-xl bg-[#F4F6F9] p-3" data-testid={`${kind}-comment-box`}>
      {estado === "rechazada" && <div className="mb-2 rounded-lg bg-[#FBECEC] text-[#C24141] text-xs font-bold p-2">Tu comentario fue devuelto para corregir.{feedback ? ` Motivo: ${feedback}` : ""} Vuelve a enviarlo.</div>}
      <label className="text-xs font-bold text-[#0A1C30] flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> Escribe tu comentario para registrar tu evidencia (+{puntos || 5} Puntos Supera)</label>
      <textarea data-testid={`${kind}-comentario`} value={texto} onChange={(e) => setTexto(e.target.value)} rows={4} placeholder="Comparte lo que aprendiste o reflexionaste…" className="mt-2 w-full rounded-xl ring-1 ring-black/10 px-3 py-2 text-sm" />
      <button data-testid={`${kind}-enviar`} onClick={enviar} disabled={busy} className="mt-2 w-full rounded-xl bg-[#188A4E] text-white font-extrabold py-2.5 disabled:opacity-60 inline-flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} <Send className="w-4 h-4" /> Enviar comentario</button>
    </div>
  );
}

function BlogTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => { try { setItems(await campusApi.blogs()); } catch (e) { toast.error(apiError(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async (id, texto) => { try { const r = await campusApi.blogComentario(id, texto); toast.success(r.message || "Enviado."); setOpen(null); await load(); } catch (e) { toast.error(apiError(e)); throw e; } };
  if (!items) return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>;
  if (items.length === 0) return <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center" data-testid="blog-empty"><Newspaper className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 text-[#6B7688] font-semibold">Aún no hay artículos del Blog Supera para tu programa.</p></div>;
  return (
    <div className="space-y-3" data-testid="blog-tab">
      {items.map((b) => {
        const st = ENTREGA_STATUS[b.estado];
        return (
          <div key={b.id} className="bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden" data-testid={`blog-item-${b.id}`}>
            <button onClick={() => setOpen(open === b.id ? null : b.id)} className="w-full text-left flex items-center gap-3 p-4 hover:bg-[#FAFBFC]">
              {b.imagen_url ? <img src={mediaUrl(b.imagen_url)} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" /> : <span className="w-16 h-16 rounded-xl bg-[#E6F7FA] grid place-items-center flex-shrink-0"><Newspaper className="w-7 h-7 text-[#0A9BAF]" /></span>}
              <div className="min-w-0 flex-1"><p className="font-extrabold text-[#0A1C30] text-sm">{b.titulo}</p>{st && <span className={`mt-1 inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>}</div>
              <BookOpen className="w-5 h-5 text-[#0A9BAF] flex-shrink-0" />
            </button>
            {open === b.id && (
              <div className="p-4 pt-0 border-t border-black/5" data-testid={`blog-reader-${b.id}`}>
                {b.imagen_url && <img src={mediaUrl(b.imagen_url)} alt="" className="w-full max-h-72 object-cover rounded-xl mt-4" />}
                {b.url ? (
                  <div className="mt-4 aspect-video w-full rounded-xl overflow-hidden ring-1 ring-black/10"><iframe src={embedSrc(b.url)} title={b.titulo} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen /></div>
                ) : null}
                {b.contenido && <div className="blog-rich mt-4 text-[15px] leading-relaxed text-[#2A3446]" dangerouslySetInnerHTML={{ __html: renderRich(b.contenido) }} />}
                <div className="mt-5"><CommentBox kind="blog" estado={b.estado} feedback={b.feedback} puntos={b.puntos} onSubmit={(t) => submit(b.id, t)} /></div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function VideosExpressTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => { try { setItems(await campusApi.videosExpress()); } catch (e) { toast.error(apiError(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async (id, texto) => { try { const r = await campusApi.videoComentario(id, texto); toast.success(r.message || "Enviado."); setOpen(null); await load(); } catch (e) { toast.error(apiError(e)); throw e; } };
  if (!items) return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>;
  if (items.length === 0) return <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center" data-testid="videos-empty"><Clapperboard className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 text-[#6B7688] font-semibold">Aún no hay Videos Express para tu programa.</p></div>;
  return (
    <div className="space-y-3" data-testid="videos-tab">
      {items.map((v) => {
        const st = ENTREGA_STATUS[v.estado];
        return (
          <div key={v.id} className="bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden" data-testid={`video-item-${v.id}`}>
            <button onClick={() => setOpen(open === v.id ? null : v.id)} className="w-full text-left flex items-center gap-3 p-4 hover:bg-[#FAFBFC]">
              {v.imagen_url ? <img src={mediaUrl(v.imagen_url)} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" /> : <span className="w-16 h-16 rounded-xl bg-[#FBF0CE] grid place-items-center flex-shrink-0"><Clapperboard className="w-7 h-7 text-[#B9860B]" /></span>}
              <div className="min-w-0 flex-1"><p className="font-extrabold text-[#0A1C30] text-sm">{v.titulo}</p>{st && <span className={`mt-1 inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>}</div>
              <PlayCircle className="w-5 h-5 text-[#E9A400] flex-shrink-0" />
            </button>
            {open === v.id && (
              <div className="p-4 pt-0 border-t border-black/5" data-testid={`video-player-${v.id}`}>
                <div className="mt-4 aspect-video w-full rounded-xl overflow-hidden ring-1 ring-black/10 bg-black">
                  {isInternalVideo(v.url)
                    ? <video src={mediaUrl(v.url)} controls controlsList="nodownload" className="w-full h-full" />
                    : <iframe src={embedSrc(v.url)} title={v.titulo} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />}
                </div>
                {v.descripcion && <p className="mt-3 text-sm text-[#2A3446] whitespace-pre-wrap">{v.descripcion}</p>}
                <div className="mt-5"><CommentBox kind="video" estado={v.estado} feedback={v.feedback} puntos={v.puntos} onSubmit={(t) => submit(v.id, t)} /></div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function LibroTab() {
  const readOnly = isParentView();
  const [libros, setLibros] = useState(null);
  const [form, setForm] = useState(null); // {libro, respuestas, comentario}
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setLibros(await campusApi.libros()); } catch (e) { toast.error(apiError(e)); } }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try { const r = await campusApi.submitLibro(form.libro.id, { respuestas: form.respuestas, comentario: form.comentario }); toast.success(r.message || "Enviado."); setForm(null); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!libros) return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>;
  if (libros.length === 0) return <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center"><BookOpen className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 text-[#6B7688] font-semibold">Aún no hay libros asignados para tu programa.</p></div>;

  return (
    <div className="space-y-4" data-testid="libro-tab">
      {libros.map((l) => {
        const e = l.entrega;
        const st = e && ENTREGA_STATUS[e.status];
        return (
          <div key={l.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-5" data-testid={`libro-${l.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-extrabold tracking-widest text-[var(--gold)]">LIBRO DEL MES · {l.mes}</p>
                <p className="font-extrabold text-[#0A1C30] text-lg">{l.titulo}</p>
                {l.autor && <p className="text-sm text-[#6B7688]">{l.autor}</p>}
                {l.fecha_evaluacion && <p className="text-xs text-[#9AA3B2] mt-1">Evaluación: {l.fecha_evaluacion}</p>}
              </div>
              {st && <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {l.enlace && <a href={l.enlace} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-xl bg-navy text-white font-bold px-3 py-2 text-xs"><BookOpen className="w-4 h-4" /> Leer libro</a>}
              {(!e || e.status === "rechazada") && !readOnly && (
                <button data-testid={`libro-eval-${l.id}`} onClick={() => setForm({ libro: l, respuestas: {}, comentario: "" })} className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--gold)] text-[#0A1C30] font-bold px-3 py-2 text-xs"><Send className="w-4 h-4" /> {e ? "Reenviar evaluación" : "Hacer evaluación"}</button>
              )}
            </div>
            {e && e.feedback && <div className="mt-3 rounded-lg bg-[#F4F6F9] px-3 py-2 text-sm"><b className="text-[#0A1C30]">Retroalimentación:</b> <span className="text-[#2A3446]">{e.feedback}</span></div>}
          </div>
        );
      })}

      {form && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[85vh] overflow-auto" onClick={(ev) => ev.stopPropagation()} data-testid="libro-modal">
            <div className="flex items-center justify-between mb-3"><p className="font-extrabold text-[#0A1C30]">Evaluación · {form.libro.titulo}</p><button onClick={() => setForm(null)}><X className="w-5 h-5 text-[#6B7688]" /></button></div>
            {(form.libro.preguntas || []).length > 0 ? (form.libro.preguntas.map((q) => (
              <div key={q.id} className="mb-3">
                <label className="block text-xs font-bold text-[#0A1C30] mb-1">{q.label}</label>
                <textarea data-testid={`libro-q-${q.id}`} rows={2} value={form.respuestas[q.id] || ""} onChange={(e2) => setForm((p) => ({ ...p, respuestas: { ...p.respuestas, [q.id]: e2.target.value } }))} className="w-full rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm" />
              </div>
            ))) : (
              <textarea data-testid="libro-comentario" rows={5} value={form.comentario} onChange={(e2) => setForm((p) => ({ ...p, comentario: e2.target.value }))} placeholder="Escribe tu reflexión sobre el libro…" className="w-full rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm" />
            )}
            <button data-testid="libro-submit" onClick={submit} disabled={busy} className="mt-3 w-full rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold py-2.5 disabled:opacity-60 inline-flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Enviar evaluación</button>
          </div>
        </div>
      )}
    </div>
  );
}

function EvalInstructorTab({ onChanged }) {
  const readOnly = isParentView();
  const [data, setData] = useState(null);
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setData(await campusApi.evalInstructores()); } catch (e) { toast.error(apiError(e)); setData({ items: [], preguntas: [] }); } }, []);
  useEffect(() => { load(); }, [load]);

  const pendientes = (data?.items || []).filter((i) => !i.enviada);
  const sel = pendientes.find((i) => i.clase_id === selId) || pendientes[0];
  const submit = async (respuestas) => {
    setBusy(true);
    try { const r = await campusApi.submitEvalInstructor({ clase_id: sel.clase_id, respuestas }); toast.success(r.message || "Enviada."); setSelId(null); await load(); onChanged?.(); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>;

  return (
    <div data-testid="eval-instructor-tab">
      <div className="rounded-2xl bg-[#F1E7FB] p-4 mb-4"><p className="text-sm text-[#5B2E92] font-semibold">Evalúa a tu instructor al terminar cada clase. <b>Es necesario completar la evaluación para poder avanzar a tu siguiente clase.</b></p></div>
      {data.items.length === 0 ? (
        <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center" data-testid="eval-inst-empty"><ClipboardCheck className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 text-[#6B7688] font-semibold">Aún no tienes clases terminadas para evaluar. La encuesta aparecerá aquí cuando termine tu próxima clase.</p></div>
      ) : (
        <>
          {sel && !readOnly && <EvalInstructorForm key={sel.clase_id} item={sel} preguntas={data.preguntas || []} busy={busy} onSubmit={submit} />}
          {sel && readOnly && <div className="rounded-2xl bg-white ring-1 ring-black/5 p-4 text-sm text-[#6B7688]">Tu hijo(a) tiene {pendientes.length} encuesta(s) pendiente(s). Solo el alumno puede responderlas desde su cuenta.</div>}
          {!sel && <div className="rounded-2xl bg-[#E2F6EB] ring-1 ring-[#22A860]/25 p-4 text-sm font-semibold text-[#1E7D48]" data-testid="eval-inst-all-done">{readOnly ? "Todas las clases terminadas ya fueron evaluadas." : "¡Listo! Ya evaluaste todas tus clases terminadas."}</div>}
          <p className="mt-6 mb-2 text-xs font-extrabold tracking-wider text-[#6B7688] uppercase">Tus clases</p>
          <div className="space-y-2">
            {data.items.map((it) => (
              <div key={it.clase_id} className={`bg-white rounded-2xl ring-1 p-4 ${sel?.clase_id === it.clase_id ? "ring-[#7B3FBF]" : "ring-black/5"}`} data-testid={`eval-item-${it.clase_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-[#0A1C30] text-sm">{it.clase_titulo || "Clase"}</p>
                    <p className="text-xs text-[#6B7688]">{it.materia_nombre ? `${it.materia_nombre} · ` : ""}{it.instructor_nombre || "Instructor"}{it.fecha ? ` · ${it.fecha}` : ""}</p>
                    <span className={`mt-1 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${it.enviada ? "bg-[#E2F6EB] text-[#22A860]" : "bg-[#FBECEC] text-[#C24141]"}`}>{it.enviada ? "Evaluada" : "Pendiente"}</span>
                  </div>
                  {!it.enviada && !readOnly && sel?.clase_id !== it.clase_id && <button data-testid={`eval-inst-btn-${it.clase_id}`} onClick={() => { setSelId(it.clase_id); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="rounded-xl bg-[#7B3FBF] text-white font-bold px-3 py-2 text-xs shrink-0">Evaluar</button>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function BibliotecaTab() {
  const [data, setData] = useState(null);
  const [tag, setTag] = useState("");
  const [player, setPlayer] = useState(null); // {titulo, url, loading, canDownload}
  const load = useCallback(async (t) => { try { setData(await campusApi.biblioteca(t)); } catch (e) { toast.error(apiError(e)); } }, []);
  useEffect(() => { load(tag); }, [load, tag]);

  const openConnect = async (g) => {
    setPlayer({ titulo: g.titulo, url: null, loading: true, canDownload: data?.descargas_habilitadas });
    try {
      const blob = await connectApi.recStreamBlob(g.recording_id);
      setPlayer({ titulo: g.titulo, url: URL.createObjectURL(blob), loading: false, canDownload: data?.descargas_habilitadas });
    } catch (e) { setPlayer(null); toast.error("No se pudo cargar la grabación."); }
  };

  if (!data) return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>;
  return (
    <div data-testid="biblioteca-tab">
      {data.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setTag("")} className={`text-xs font-bold px-3 py-1.5 rounded-full ${!tag ? "bg-navy text-white" : "bg-[#EDF0F4] text-[#6B7688]"}`}>Todas</button>
          {data.tags.map((t) => <button key={t} data-testid={`tag-${t}`} onClick={() => setTag(t)} className={`text-xs font-bold px-3 py-1.5 rounded-full ${tag === t ? "bg-navy text-white" : "bg-[#EDF0F4] text-[#6B7688]"}`}>{t}</button>)}
        </div>
      )}
      {data.grabaciones.length === 0 ? (
        <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center"><Library className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 text-[#6B7688] font-semibold">Aún no hay grabaciones en la biblioteca.</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {data.grabaciones.map((g) => (
            <div key={g.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-4" data-testid={`grabacion-${g.id}`} onContextMenu={(e) => e.preventDefault()}>
              <p className="font-extrabold text-[#0A1C30] text-sm flex items-center gap-1.5">{g.es_connect && <Radio className="w-3.5 h-3.5 text-[#C0140F]" />}{g.titulo}</p>
              {g.descripcion && <p className="text-xs text-[#6B7688] mt-0.5 line-clamp-2">{g.descripcion}</p>}
              {(g.tags || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1">{g.tags.map((t) => <span key={t} className="text-[10px] font-bold bg-[#EDF2FF] text-[#2F5BEA] rounded-full px-2 py-0.5">{t}</span>)}</div>}
              {g.es_connect ? (
                <button onClick={() => openConnect(g)} data-testid={`ver-connect-${g.id}`} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-navy text-white font-bold px-3 py-2 text-xs"><PlayCircle className="w-4 h-4" /> Ver clase grabada</button>
              ) : (
                <a href={mediaUrl(g.url)} target="_blank" rel="noreferrer" onContextMenu={(e) => e.preventDefault()} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-navy text-white font-bold px-3 py-2 text-xs"><PlayCircle className="w-4 h-4" /> Ver grabación</a>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="mt-4 text-[11px] text-[#9AA3B2] text-center">Contenido protegido de Supera Leadership. Solo para visualización; no está permitida su descarga ni difusión.</p>

      {player && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => { if (player.url) URL.revokeObjectURL(player.url); setPlayer(null); }} data-testid="connect-player">
          <div className="bg-[#0A1420] rounded-2xl overflow-hidden w-full max-w-3xl" onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.preventDefault()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <p className="font-extrabold text-white text-sm truncate flex items-center gap-2"><Radio className="w-4 h-4 text-[#C0140F]" /> {player.titulo}</p>
              <button onClick={() => { if (player.url) URL.revokeObjectURL(player.url); setPlayer(null); }} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="bg-black flex items-center justify-center min-h-[240px] relative">
              <img src="/logo-supera-light.png" alt="Supera" className="absolute top-3 right-3 h-6 opacity-70 pointer-events-none select-none z-10" />
              {player.loading || !player.url ? (
                <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)] my-16" />
              ) : (
                <video src={player.url} controls autoPlay controlsList={player.canDownload ? "" : "nodownload noplaybackrate"} disablePictureInPicture onContextMenu={(e) => e.preventDefault()} className="w-full max-h-[70vh]" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreguntasInstructorTab() {
  const readOnly = isParentView();
  const [instructores, setInstructores] = useState([]);
  const [msgs, setMsgs] = useState(null);
  const [f, setF] = useState({ instructor_id: "", tipo: "pregunta", texto: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [ev, mm, claustro] = await Promise.all([
        campusApi.evalInstructores().catch(() => ({ items: [] })),
        campusApi.instructorMensajes().catch(() => []),
        campusApi.claustro().catch(() => []),
      ]);
      const uniq = {};
      (claustro || []).forEach((i) => { uniq[i.id] = `${i.nombre || ""} ${i.apellidos || ""}`.trim(); });
      (ev.items || []).forEach((it) => { if (it.instructor_id) uniq[it.instructor_id] = it.instructor_nombre || uniq[it.instructor_id]; });
      (mm || []).forEach((m) => { if (m.instructor_id && !uniq[m.instructor_id]) uniq[m.instructor_id] = ""; });
      const list = Object.entries(uniq).map(([id, nombre]) => ({ id, nombre: nombre || "Instructor" }));
      setInstructores(list); setF((s) => ({ ...s, instructor_id: s.instructor_id || list[0]?.id || "" })); setMsgs(mm || []);
    } catch (e) { toast.error(apiError(e)); setMsgs([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!f.instructor_id) { toast.error("Selecciona un instructor."); return; }
    if (!f.texto.trim()) { toast.error("Escribe tu pregunta o comentario."); return; }
    setBusy(true);
    try { const r = await campusApi.postInstructorMensaje(f); toast.success(r.message || "Enviado."); setF((s) => ({ ...s, texto: "" })); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const badge = (m) => m.estado === "autorizado" ? ["Publicado", "bg-[#E2F6EB] text-[#22A860]"] : m.estado === "negado" ? ["No aprobado", "bg-[#FBECEC] text-[#C24141]"] : ["En revisión", "bg-[#FBF0CE] text-[#B9860B]"];
  return (
    <div data-testid="preguntas-instructor-tab">
      <div className="rounded-2xl bg-[#EAF0FF] p-4 mb-4"><p className="text-sm text-[#2F4B8F] font-semibold">Este espacio es para tus preguntas o comentarios de clase hacia el instructor. Tu mensaje será revisado por la coordinación antes de llegar al instructor.</p></div>
      {instructores.length === 0 ? (
        <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center"><HelpCircle className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 text-[#6B7688] font-semibold">Aún no tienes instructores asignados.</p></div>
      ) : (
        <>
          {!readOnly && (
          <div className="bg-white rounded-2xl ring-1 ring-black/5 p-4 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <select data-testid="pregunta-instructor" value={f.instructor_id} onChange={(e) => setF((s) => ({ ...s, instructor_id: e.target.value }))} className="rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm bg-white">
                {instructores.map((i) => <option key={i.id} value={i.id}>{i.nombre}</option>)}
              </select>
              <select value={f.tipo} onChange={(e) => setF((s) => ({ ...s, tipo: e.target.value }))} className="rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm bg-white"><option value="pregunta">Pregunta</option><option value="comentario">Comentario</option></select>
            </div>
            <textarea data-testid="pregunta-texto" value={f.texto} onChange={(e) => setF((s) => ({ ...s, texto: e.target.value }))} rows={3} placeholder="Escribe tu pregunta o comentario…" className="w-full rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm" />
            <button data-testid="pregunta-send" onClick={send} disabled={busy} className="rounded-xl bg-[#2F5BEA] text-white font-extrabold px-5 py-2.5 disabled:opacity-60 inline-flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} <Send className="w-4 h-4" /> Enviar</button>
          </div>
          )}
          <div className="mt-5 space-y-2" data-testid="mis-preguntas-list">
            {(msgs || []).length === 0 ? <p className="text-sm text-[#6B7688]">Aún no has enviado mensajes.</p> : msgs.map((m) => {
              const [b, cls] = badge(m);
              return (
                <div key={m.id} className={`rounded-xl px-3 py-2.5 text-sm ${m.autor === "instructor" ? "bg-[#EDF2FF] ml-6" : "bg-white ring-1 ring-black/5 mr-6"}`}>
                  <div className="flex items-center justify-between gap-2"><p className="text-[10px] font-bold uppercase text-[#9AA3B2]">{m.autor === "instructor" ? "Respuesta del instructor" : "Tú"}</p>{m.autor === "candidato" && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{b}</span>}</div>
                  <p className="text-[#2A3446] mt-0.5">{m.texto}</p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default function CampusUser() {
  const navigate = useNavigate();
  const readOnly = isParentView();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(null);
  const [busy, setBusy] = useState("");
  const [evalModal, setEvalModal] = useState(null); // {clase, prompt, texto}
  const [evidModal, setEvidModal] = useState(null); // {clase, comentario}

  const load = useCallback(async () => {
    try { setData(await campusApi.overview()); } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const marcarVisto = async (claseId) => {
    setBusy(claseId);
    try { await campusApi.marcarVisto(claseId); await load(); toast.success("Sesión marcada como vista."); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const openEval = async (c) => {
    try { const r = await campusApi.getEvaluacion(c.id); setEvalModal({ clase: c, prompt: r.prompt, texto: r.evaluacion?.texto || "" }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submitEval = async () => {
    setBusy("eval");
    try { await campusApi.submitEvaluacion(evalModal.clase.id, evalModal.texto); toast.success("Evaluación guardada."); setEvalModal(null); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const submitEvid = async () => {
    setBusy("evid");
    try { const r = await campusApi.submitEvidencia(evidModal.clase.id, { comentario: evidModal.comentario }); toast.success(r.message || "Evidencia enviada."); setEvidModal(null); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const addToCalendar = async (c) => {
    setBusy(`ics-${c.id}`);
    try { await campusApi.downloadIcs(c.id, `supera-${(c.titulo || "sesion").slice(0, 24)}.ics`); toast.success("Invitación de calendario descargada."); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="min-h-screen bg-cream grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-[#E9A400]" /></div>;

  const u = data.usuario;
  const TABS = [
    ["superaconnect", "Supera Connect", Radio, "#C0140F", "https://images.unsplash.com/photo-1594394489098-74ac04c0fc2e?crop=entropy&cs=srgb&fm=jpg&q=70&w=500&ixlib=rb-4.1.0"],
    ["proximas", "Próximas sesiones", CalendarClock, "#E9A400", "https://images.unsplash.com/photo-1577896851231-70ef18881754?crop=entropy&cs=srgb&fm=jpg&q=70&w=500&ixlib=rb-4.1.0"],
    ["grabadas", "Sesiones grabadas", Video, "#8E44AD", "https://images.unsplash.com/photo-1588702547954-4800ead296ef?crop=entropy&cs=srgb&fm=jpg&q=70&w=500&ixlib=rb-4.1.0"],
    ["evalinst", "Evaluar instructor", ClipboardCheck, "#7B3FBF", "https://images.unsplash.com/photo-1754039985008-a15410211b67?crop=entropy&cs=srgb&fm=jpg&q=70&w=500&ixlib=rb-4.1.0"],
    ["preguntas", "Preguntas al instructor", HelpCircle, "#2F5BEA", "https://images.unsplash.com/photo-1509062522246-3755977927d7?crop=entropy&cs=srgb&fm=jpg&q=70&w=500&ixlib=rb-4.1.0"],
    ["libro", "Libro del mes", BookOpen, "#0A9BAF", "https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?crop=entropy&cs=srgb&fm=jpg&q=70&w=500&ixlib=rb-4.1.0"],
    ["biblioteca", "Biblioteca", Library, "#22A860", "https://images.unsplash.com/photo-1603058817990-2b9a9abbce86?crop=entropy&cs=srgb&fm=jpg&q=70&w=500&ixlib=rb-4.1.0"],
    ...(showBlogVideo(u.programa) ? [
      ["blog", "Blog Supera", Newspaper, "#0A9BAF", "https://images.unsplash.com/photo-1610116306796-6fea9f4fae38?crop=entropy&cs=srgb&fm=jpg&q=70&w=500&ixlib=rb-4.1.0"],
      ["videos", "Videos Express", Clapperboard, "#E9A400", "https://images.unsplash.com/photo-1557825835-70d97c4aa567?crop=entropy&cs=srgb&fm=jpg&q=70&w=500&ixlib=rb-4.1.0"],
    ] : []),
    ["avisos", "Avisos importantes", Megaphone, "#C0392B", "https://images.unsplash.com/photo-1517816428104-797678c7cf0c?crop=entropy&cs=srgb&fm=jpg&q=70&w=500&ixlib=rb-4.1.0"],
  ];

  const SessionCard = ({ c, recorded, showMateria }) => {
    const st = STATUS[c.estado] || STATUS.pendiente;
    const fecha = c.fecha_local || c.fecha;
    const hora = c.hora_local || c.hora;
    return (
      <div className={`rounded-xl ring-1 ring-black/5 bg-white p-4 flex items-start justify-between gap-3 ${c.bloqueada ? "opacity-70" : ""}`} data-testid={`sesion-${c.id}`}>
        <div className="min-w-0">
          <p className="font-extrabold text-[#0A1C30] text-sm">{c.titulo}{c.grupo ? <span className="ml-1.5 text-[11px] font-bold text-[#6B7688]">· {c.grupo}</span> : null}</p>
          {showMateria && c.materia_nombre && <p className="text-[11px] font-bold text-[#B9860B] mt-0.5 flex items-center gap-1"><span className="w-1 h-3 rounded-full bg-[var(--gold)]" /> {c.materia_nombre}</p>}
          <p className="text-xs text-[#6B7688] mt-0.5 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> {fecha} · {hora}{c.tz_local ? ` (${c.tz_local.split("/").pop().replace(/_/g, " ")})` : ""}{c.instructor ? ` · ${c.instructor}` : ""}</p>
          {c.bloqueada ? (
            <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#F1E7FB] text-[#7B3FBF]" data-testid={`bloqueada-${c.id}`}><Lock className="w-3 h-3" /> Bloqueada</span>
          ) : (
            <span className={`mt-2 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}><st.Icon className="w-3 h-3" /> {st.label}</span>
          )}
          {c.bloqueada && <p className="text-[11px] text-[#7B3FBF] mt-1">{c.bloqueo_motivo}</p>}
          {!recorded && !c.bloqueada && c.visto && (
            <span className={`mt-2 ml-1.5 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${c.evaluacion_hecha ? "bg-[#E2F6EB] text-[#22A860]" : "bg-[#EDF0F4] text-[#6B7688]"}`}>{c.evaluacion_hecha ? "Evaluación hecha" : "Evaluación pendiente"}</span>
          )}
          {(c.materiales || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`materiales-${c.id}`}>
              {c.materiales.map((m) => (
                <a key={m.id} href={mediaUrl(m.url)} target="_blank" rel="noreferrer" onContextMenu={(e) => e.preventDefault()} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#2F5BEA] bg-[#EDF2FF] rounded-full px-2 py-0.5"><Paperclip className="w-3 h-3" /> {m.titulo}</a>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {!recorded && !c.bloqueada && c.connect_live && (
            <button onClick={() => navigate(`/connect/room/${c.connect_session_id}`)} data-testid={`connect-live-${c.id}`} className="inline-flex items-center gap-1.5 rounded-xl bg-[#C0140F] text-white font-bold px-3 py-2 text-xs animate-pulse hover:bg-[#a01109]"><Radio className="w-4 h-4" /> EN VIVO</button>
          )}
          {recorded && c.grabacion_url && (
            <a href={c.grabacion_url} target="_blank" rel="noreferrer" onContextMenu={(e) => e.preventDefault()} className="inline-flex items-center gap-1.5 rounded-xl bg-navy text-white font-bold px-3 py-2 text-xs hover:bg-[#0E2740]"><PlayCircle className="w-4 h-4" /> Ver</a>
          )}
          {!recorded && !c.bloqueada && c.zoom_url && (
            <a href={c.zoom_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--gold)] text-[#0A1C30] font-bold px-3 py-2 text-xs"><Video className="w-4 h-4" /> Entrar</a>
          )}
          {!recorded && !c.bloqueada && (
            <button data-testid={`ics-${c.id}`} onClick={() => addToCalendar(c)} disabled={busy === `ics-${c.id}`} className="inline-flex items-center gap-1.5 rounded-xl bg-[#EDF2FF] text-[#2F5BEA] font-bold px-3 py-2 text-xs disabled:opacity-60"><CalendarPlus className="w-4 h-4" /> Calendario</button>
          )}
          {!readOnly && !c.bloqueada && !c.visto && (
            c.ya_ocurrio ? (
              <button data-testid={`visto-${c.id}`} onClick={() => marcarVisto(c.id)} disabled={busy === c.id} className="rounded-xl bg-[#F4F6F9] text-[#0A1C30] font-bold px-3 py-2 text-xs disabled:opacity-60">Marcar vista</button>
            ) : (
              <span data-testid={`visto-locked-${c.id}`} title="Podrás marcarla cuando llegue la fecha y hora de la clase" className="inline-flex items-center gap-1.5 rounded-xl bg-[#EDF0F4] text-[#9AA3B2] font-bold px-3 py-2 text-xs cursor-not-allowed"><Lock className="w-3.5 h-3.5" /> Aún no disponible</span>
            )
          )}
          {!readOnly && !recorded && !c.bloqueada && c.visto && (
            <button data-testid={`eval-${c.id}`} onClick={() => openEval(c)} className="inline-flex items-center gap-1.5 rounded-xl bg-[#EFEAFB] text-[#7B3FBF] font-bold px-3 py-2 text-xs"><BookOpen className="w-4 h-4" /> {c.evaluacion_hecha ? "Ver evaluación" : "Evaluación"}</button>
          )}
          {!readOnly && !recorded && c.puede_evidencia && (
            <button data-testid={`evid-${c.id}`} onClick={() => setEvidModal({ clase: c, comentario: "" })} className="inline-flex items-center gap-1.5 rounded-xl bg-[#E2F6EB] text-[#188A4E] font-bold px-3 py-2 text-xs"><Send className="w-4 h-4" /> Enviar evidencia</button>
          )}
          {!recorded && c.evidencia_enviada && (
            <span className="inline-flex items-center gap-1.5 rounded-xl bg-[#FBF0CE] text-[#B9860B] font-bold px-3 py-2 text-xs"><CheckCircle2 className="w-4 h-4" /> Evidencia en revisión</span>
          )}
        </div>
      </div>
    );
  };

  const groups = tab === "grabadas" ? data.grabadas : [];
  const proxList = data.proximas_lista || [];
  const addDaysStr = (d, n) => { const x = new Date(d + "T00:00:00"); x.setDate(x.getDate() + n); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
  const bucketLabel = (fecha) => {
    const ws = data.progreso.week_start;
    if (ws) {
      const w2 = addDaysStr(ws, 7), w3 = addDaysStr(ws, 14);
      if (fecha >= ws && fecha < w2) return "Esta semana";
      if (fecha >= w2 && fecha < w3) return "Próxima semana";
    }
    const [y, m] = fecha.split("-");
    const lbl = `${MONTHS[Number(m)] || ""} ${y}`;
    return lbl.charAt(0).toUpperCase() + lbl.slice(1);
  };

  return (
    <div className="min-h-screen bg-cream pb-16" data-testid="campus-page">
      <header className="bg-navy">
        <div className="max-w-4xl mx-auto px-6 h-20 flex items-center justify-between">
          <button data-testid="campus-back" onClick={() => navigate("/mi-cuenta")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold"><ArrowLeft className="w-4 h-4" /> Mi cuenta</button>
          <img src="/logo-supera-light.png" alt="Supera" className="h-11 w-auto object-contain" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <ParentViewBar />
        {/* Header card */}
        <div className="rounded-3xl bg-gradient-to-br from-[#0A1C30] to-[#1c3e5f] p-6 md:p-8 text-white relative overflow-hidden">
          <GraduationCap className="absolute -right-4 -top-4 w-28 h-28 text-white/5" />
          <p className="text-xs font-extrabold tracking-[0.25em] text-[var(--gold)]">CAMPUS EN LÍNEA</p>
          <h1 className="text-2xl md:text-3xl font-extrabold mt-1">{u.nombre}</h1>
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            <span className="rounded-full bg-white/10 px-3 py-1 font-bold">{u.programa}</span>
            {u.referencia && <span className="rounded-full bg-white/10 px-3 py-1">Referencia Supera: {u.referencia}</span>}
            <span className="rounded-full bg-white/10 px-3 py-1">Inicio: {fmtMonth(u.inicio)}</span>
            <span className="rounded-full bg-white/10 px-3 py-1">Fin: {fmtMonth(u.fin)}</span>
            <span className="rounded-full bg-[var(--gold)] text-[#0A1C30] px-3 py-1 font-bold">{u.duracion_meses} meses</span>
          </div>
          {/* Weekly progress */}
          <div className="mt-5">
            <div className="flex justify-between text-sm font-bold mb-1.5"><span>Avance semanal</span><span data-testid="campus-progress">{data.progreso.pct}%</span></div>
            <div className="h-3 rounded-full bg-white/15 overflow-hidden"><div className="h-full rounded-full bg-[var(--gold)] transition-all duration-500" style={{ width: `${data.progreso.pct}%` }} /></div>
            <p className="text-xs text-white/60 mt-1.5">{data.progreso.hechas} de {data.progreso.total} sesiones de esta semana · reinicia cada domingo 2:00 AM</p>
          </div>
        </div>

        {/* Motivational phrase */}
        <div className="mt-4 rounded-2xl bg-white ring-1 ring-black/5 p-4 flex items-start gap-3" data-testid="campus-frase">
          <Quote className="w-6 h-6 text-[var(--gold)] flex-shrink-0" />
          <p className="text-sm italic text-[#2A3446] font-medium">{data.frase}</p>
        </div>

        {/* Help CTA */}
        <button data-testid="campus-help" onClick={() => navigate("/chat")} className="mt-4 w-full rounded-2xl bg-[#FBF0CE] ring-1 ring-[#E9A400]/30 p-4 flex items-center gap-3 text-left hover:shadow-md transition-all">
          <HelpCircle className="w-6 h-6 text-[#B9860B] flex-shrink-0" />
          <div className="flex-1"><p className="font-bold text-[#8A6D0B] text-sm">¿Necesitas ayuda? Clic aquí</p><p className="text-xs text-[#8A6D0B]">Te sugerimos primero al Asistente Virtual; también puedes hablar con el staff Supera.</p></div>
        </button>

        {/* Icon menu */}
        {!tab ? (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="campus-menu">
            {TABS.map(([v, l, Ic, color, img]) => {
              const badge = (data.pendientes || {})[v] || 0;
              return (
                <button key={v} data-testid={`campus-tab-${v}`} onClick={() => v === "superaconnect" ? navigate("/en-vivo") : setTab(v)} className="group relative overflow-hidden rounded-2xl ring-1 ring-black/5 shadow-sm text-left transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] min-h-[132px] sm:min-h-[152px]">
                  <img src={img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute inset-0 bg-gradient-to-t from-[#08182b] via-[#0a1c30]/70 to-[#0a1c30]/20" />
                  {badge > 0 && <span data-testid={`badge-${v}`} className="absolute top-3 right-3 z-20 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#E23B3B] text-white text-[11px] font-extrabold grid place-items-center ring-2 ring-white">{badge}</span>}
                  <span className="relative z-10 flex flex-col h-full p-4 sm:p-5">
                    <span className="w-11 h-11 rounded-xl grid place-items-center bg-white/15 ring-1 ring-white/25 backdrop-blur-sm"><Ic className="w-6 h-6 text-white" /></span>
                    <span className="mt-auto pt-6">
                      <p className="font-extrabold text-white text-sm leading-tight drop-shadow-md">{l}</p>
                      {badge > 0 && <p className="text-[11px] font-bold text-[#FFD27A] mt-0.5 drop-shadow">{badge} pendiente{badge > 1 ? "s" : ""}</p>}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <>
            <button data-testid="campus-menu-back" onClick={() => setTab(null)} className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#6B7688] hover:text-[#0A1C30]"><ArrowLeft className="w-4 h-4" /> Volver al panel</button>

            <div className="mt-5">
              {tab === "evalinst" ? (
                <EvalInstructorTab onChanged={load} />
              ) : tab === "blog" ? (
                <BlogTab />
              ) : tab === "videos" ? (
                <VideosExpressTab />
              ) : tab === "preguntas" ? (
                <PreguntasInstructorTab />
              ) : tab === "libro" ? (
                <LibroTab />
              ) : tab === "biblioteca" ? (
                <BibliotecaTab />
              ) : tab === "avisos" ? (
                <div className="space-y-3">
                  {data.avisos.length === 0 && <p className="text-sm text-[#6B7688]">No hay avisos por ahora.</p>}
                  {data.avisos.map((a) => (
                    <div key={a.id} className="rounded-2xl bg-white ring-1 ring-black/5 p-4" data-testid={`aviso-${a.id}`}>
                      <p className="font-extrabold text-[#0A1C30] flex items-center gap-2"><Megaphone className="w-4 h-4 text-[#E9A400]" /> {a.titulo}</p>
                      <p className="text-sm text-[#2A3446] mt-1">{a.mensaje}</p>
                      <p className="text-xs text-[#9AA3B2] mt-1">{a.fecha}</p>
                    </div>
                  ))}
                </div>
              ) : tab === "proximas" ? (
                proxList.length === 0 ? (
                  <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center"><Sparkles className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 text-[#6B7688] font-semibold">No tienes sesiones próximas programadas.</p></div>
                ) : (
                  <div className="space-y-2" data-testid="proximas-lista">
                    {(() => {
                      const counts = {};
                      proxList.forEach((c) => { const b = bucketLabel(c.fecha_local || c.fecha); counts[b] = (counts[b] || 0) + 1; });
                      let last = null;
                      return proxList.map((c) => {
                        const b = bucketLabel(c.fecha_local || c.fecha);
                        const showHeader = b !== last;
                        last = b;
                        const n = counts[b];
                        return (
                          <React.Fragment key={c.id}>
                            {showHeader && (
                              <h3 data-testid={`prox-bucket-${b.toLowerCase().replace(/\s+/g, "-")}`} className="text-[13px] font-extrabold text-[#0A1C30] uppercase tracking-wide flex items-center gap-2 pt-3 first:pt-0">
                                <span className="w-1.5 h-4 rounded-full bg-[var(--gold)]" /> {b} <span className="text-[#9AA3B2] normal-case tracking-normal font-bold">· {n} clase{n > 1 ? "s" : ""}</span>
                              </h3>
                            )}
                            <SessionCard c={c} recorded={false} showMateria />
                          </React.Fragment>
                        );
                      });
                    })()}
                  </div>
                )
              ) : groups.length === 0 ? (
                <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center"><Video className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 text-[#6B7688] font-semibold">Aún no hay sesiones grabadas disponibles.</p></div>
              ) : (
                <div className="space-y-6">
                  {groups.map((g) => (
                    <div key={g.materia.id} data-testid={`materia-${g.materia.id}`}>
                      <h2 className="font-extrabold text-[#0A1C30] text-lg mb-2 flex items-center gap-2"><span className="w-1.5 h-5 rounded-full bg-[var(--gold)]" /> {g.materia.nombre}</h2>
                      <div className="space-y-2">{g.clases.map((c) => <SessionCard key={c.id} c={c} recorded />)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {evalModal && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setEvalModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="eval-modal">
            <div className="flex items-center justify-between mb-3"><p className="font-extrabold text-[#0A1C30] flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#7B3FBF]" /> Evaluación de aprendizaje</p><button onClick={() => setEvalModal(null)}><X className="w-5 h-5 text-[#6B7688]" /></button></div>
            <p className="text-sm text-[#6B7688] mb-3">{evalModal.prompt}</p>
            <textarea data-testid="eval-texto" value={evalModal.texto} onChange={(e) => setEvalModal((p) => ({ ...p, texto: e.target.value }))} rows={6} placeholder="Escribe aquí lo que aprendiste…" className="w-full rounded-xl ring-1 ring-black/10 px-3 py-2 text-sm" />
            <p className="text-[11px] text-[#9AA3B2] mt-1">Completa esta evaluación para poder enviar tu evidencia y ganar Puntos Graduación.</p>
            <button data-testid="eval-submit" onClick={submitEval} disabled={busy === "eval"} className="mt-4 w-full rounded-xl bg-[#7B3FBF] text-white font-extrabold py-2.5 disabled:opacity-60 inline-flex items-center justify-center gap-2">{busy === "eval" && <Loader2 className="w-4 h-4 animate-spin" />} Guardar evaluación</button>
          </div>
        </div>
      )}

      {evidModal && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setEvidModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="evid-modal">
            <div className="flex items-center justify-between mb-3"><p className="font-extrabold text-[#0A1C30] flex items-center gap-2"><Send className="w-5 h-5 text-[#188A4E]" /> Enviar evidencia</p><button onClick={() => setEvidModal(null)}><X className="w-5 h-5 text-[#6B7688]" /></button></div>
            <p className="text-sm text-[#6B7688] mb-3">Cuéntanos cómo aplicaste lo aprendido en esta sesión. Tu evidencia será revisada por el staff para otorgarte tus Puntos Graduación.</p>
            <textarea data-testid="evid-comentario" value={evidModal.comentario} onChange={(e) => setEvidModal((p) => ({ ...p, comentario: e.target.value }))} rows={5} placeholder="Describe tu evidencia…" className="w-full rounded-xl ring-1 ring-black/10 px-3 py-2 text-sm" />
            <button data-testid="evid-submit" onClick={submitEvid} disabled={busy === "evid"} className="mt-4 w-full rounded-xl bg-[#188A4E] text-white font-extrabold py-2.5 disabled:opacity-60 inline-flex items-center justify-center gap-2">{busy === "evid" && <Loader2 className="w-4 h-4 animate-spin" />} Enviar evidencia</button>
          </div>
        </div>
      )}
    </div>
  );
}
